import {
  SegmentDataStructureField,
  segmentDataStructureTypeToDataStructure,
} from "@redotech/redo-customer-segments-common/marketing/segments/segment-data-structures";
import { TimeframeConditionType } from "@redotech/redo-customer-segments-common/marketing/segments/segment-timeframe";
import {
  CustomerActivityType,
  CustomerDataSource,
} from "@redotech/redo-customer-segments-common/marketing/segments/segment-types";
import {
  ComparisonType,
  ListCompareOperators,
  NumericCompareOperator,
  ProximityCompareOperators,
  ProximityUnit,
  TokenCompareOperators,
  WhereConditionDataType,
} from "@redotech/redo-customer-segments-common/segments/segment-where-condition";
import XIcon from "@redotech/redo-icons/x-close.svg";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { Flex } from "@redotech/redo-web/flex";
import { remove, replace } from "@redotech/util/array";
import { assertNever } from "@redotech/util/type";
import { memo, useMemo } from "react";
import { getSegmentDataStructureField } from "../characteristics/customer-characteristics";
import { FilterConfig } from "../data-filters/filter-config";
import { SegmentBuilderText } from "../segment-builder-text";
import { WhereConditionInProgress } from "../segment-in-progress";
import { SegmentActivityWhereConditionSelector } from "./segment-activity-where-condition-selector";

export const SegmentActivityWhereConditions = memo(
  function SegmentActivityWhereConditions({
    whereConditions,
    setWhereConditions,
    activityType,
    availableDataSourcesForOrders,
    availableDataSourcesForMarketing,
  }: {
    whereConditions: WhereConditionInProgress<string>[];
    setWhereConditions: (
      whereConditions: WhereConditionInProgress<string>[],
    ) => void;
    activityType: CustomerActivityType;
    availableDataSourcesForOrders?: CustomerDataSource[];
    availableDataSourcesForMarketing?: CustomerDataSource[];
  }) {
    return (
      <>
        {whereConditions.map((condition, index) => (
          <SegmentActivityWhereCondition
            activityType={activityType}
            availableDataSourcesForMarketing={availableDataSourcesForMarketing}
            availableDataSourcesForOrders={availableDataSourcesForOrders}
            condition={condition}
            index={index}
            key={index}
            removeCondition={() => {
              const newConditions = remove(whereConditions, index);
              setWhereConditions(newConditions);
            }}
            setCondition={(newCondition) => {
              const newConditions = replace(
                whereConditions,
                index,
                newCondition,
              );
              setWhereConditions(newConditions);
            }}
          />
        ))}
      </>
    );
  },
);

export const SegmentActivityWhereCondition = memo(
  function SegmentActivityWhereCondition({
    index,
    condition,
    setCondition,
    activityType,
    removeCondition,
    availableDataSourcesForOrders,
    availableDataSourcesForMarketing,
  }: {
    index: number;
    condition: WhereConditionInProgress<string>;
    setCondition: (condition: WhereConditionInProgress<string>) => void;
    activityType: CustomerActivityType;
    removeCondition: () => void;
    availableDataSourcesForOrders?: CustomerDataSource[];
    availableDataSourcesForMarketing?: CustomerDataSource[];
  }) {
    const text = index === 0 ? "where" : "and";

    const currentField = useMemo(() => {
      return getSegmentDataStructureField(activityType, condition.dimension);
    }, [activityType, condition.dimension]);

    return (
      <Flex align="center" pl="3xl" wrap="wrap">
        <SegmentBuilderText>{text}</SegmentBuilderText>
        <SegmentActivityWhereConditionSelector
          activityType={activityType}
          availableDataSourcesForMarketing={availableDataSourcesForMarketing}
          availableDataSourcesForOrders={availableDataSourcesForOrders}
          selectedField={currentField}
          setSelectedField={(field) => {
            if (field === currentField) {
              return;
            }
            const defaultCondition =
              generateDefaultWhereConditionForField(field);
            setCondition(defaultCondition);
          }}
        />
        <FilterConfig
          condition={condition}
          dataStructure={activityType}
          setCondition={setCondition}
        />
        <RedoButton
          aria-label="Remove condition"
          IconLeading={XIcon}
          onClick={removeCondition}
        />
      </Flex>
    );
  },
);

export function generateDefaultWhereCondition(
  type: CustomerActivityType,
): WhereConditionInProgress<string> | undefined {
  const schema = segmentDataStructureTypeToDataStructure[type];
  if (!schema) {
    return undefined;
  }

  const fields = Array.from(Object.values(schema.fields));

  const firstField = fields[0];

  if (!firstField) {
    return undefined;
  }

  return generateDefaultWhereConditionForField(firstField);
}

function generateDefaultWhereConditionForField<T extends string>(
  field: SegmentDataStructureField<T>,
): WhereConditionInProgress<T> {
  switch (field.dataType) {
    case WhereConditionDataType.TOKEN: {
      return {
        type: field.dataType,
        dimension: field.type,
        comparison: {
          type: ComparisonType.TOKEN,
          operator: TokenCompareOperators.ANY,
          values: [],
        },
      };
    }
    case WhereConditionDataType.NUMERIC: {
      return {
        type: field.dataType,
        dimension: field.type,
        comparison: {
          type: ComparisonType.NUMERIC,
          operator: NumericCompareOperator.GT,
          value: 0,
        },
      };
    }
    case WhereConditionDataType.BOOLEAN: {
      return {
        type: field.dataType,
        dimension: field.type,
        comparison: { type: ComparisonType.BOOLEAN, value: false },
      };
    }
    case WhereConditionDataType.DATE_FULL: {
      return {
        type: field.dataType,
        dimension: field.type,
        comparison: { type: TimeframeConditionType.BEFORE },
      };
    }
    case WhereConditionDataType.DATE_ANNUAL: {
      return {
        type: field.dataType,
        dimension: field.type,
        comparison: { type: TimeframeConditionType.BEFORE },
      };
    }
    case WhereConditionDataType.TOKEN_LIST: {
      return {
        type: field.dataType,
        dimension: field.type,
        comparison: {
          type: ComparisonType.LIST,
          operator: ListCompareOperators.ANY,
          values: [],
        },
      };
    }
    case WhereConditionDataType.TOKEN_HIERARCHY: {
      return {
        type: field.dataType,
        dimension: field.type,
        comparison: {
          type: ComparisonType.TOKEN,
          operator: TokenCompareOperators.ANY,
          values: [],
          prerequisiteValues: [],
        },
      };
    }
    case WhereConditionDataType.CITY_PROXIMITY: {
      return {
        type: field.dataType,
        dimension: field.type,
        comparison: {
          type: ComparisonType.PROXIMITY,
          operator: ProximityCompareOperators.WITHIN,
          prerequisiteValues: [],
          value: "",
          options: { radius: 25, units: ProximityUnit.MILES },
        },
      };
    }
    case WhereConditionDataType.CUSTOM_FIELD: {
      return {
        type: field.dataType,
        dimension: field.type,
        declarationId: "", // This will need to be selected in the UI
        comparison: {
          type: ComparisonType.TOKEN,
          operator: TokenCompareOperators.ANY,
          values: [],
        },
      };
    }
    case WhereConditionDataType.STRING:
      throw new Error("String conditions are not supported for activities");
    default:
      assertNever(field.dataType);
  }
}
